import { Title, Text, Stack } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { UIFlightData } from "@flights/types";
import { localizationUtils } from "@flights/web-api-utils-universal";
import Frame from "../../Frame";
import useFormatPrice from "../../../../hooks/useFormatPrice";
import useLocaleContext from "../../../../hooks/useLocaleContext";
import useUserAgent from "../../../../hooks/useUserAgent";
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { getSearchCriteria } from "../../../../store/searchCriteria/selectors";
import getLowestAdultTravellerPrice from "../../../../utils/lowest-adult-traveller-price";
import { isOfMetaOrigin } from "../../../../utils/marketing-url-params";
import { useTrackUnifiedPriceTag } from "../../../../utils/experiments/pricing/flights_unified_price_tag";
import { UnifiedPriceTag, type UnifiedPriceTagProps } from "../../UnifiedPriceTag";
import { getAttachedAncillaries } from "../../UnifiedPriceBreakdown";
import { useGeniusMerchBlockoutExperiment } from "../../../../utils/experiments/genius/flights_genius_merch_blockout";

type Props = {
  flight: UIFlightData;
};

const FlightCardPriceOutbound = ({ flight }: Props) => {
  const { isMobile } = useUserAgent();
  const i18n = useI18n();
  const { isRTL } = useLocaleContext();
  const { adults, children } = useSelector(getSearchCriteria);
  const { formatPrice } = useFormatPrice();
  const { trackUnifiedPriceTag, trackUnifiedPriceTagStages } = useTrackUnifiedPriceTag(); // flights_unified_price_tag

  // START - flights_genius_merch_blockout
  const isGeniusMerchBlockoutEnabled = useGeniusMerchBlockoutExperiment({
    bcomPricingItems: flight.priceBreakdown?.bcomPricingItems
  });
  // END - flights_genius_merch_blockout

  const { priceBreakdown, travellerPrices } = flight;

  const roundPrice = !isOfMetaOrigin() && isMobile;
  const adultTravellerPrice = getLowestAdultTravellerPrice(travellerPrices);
  const travellersCount = adults + children.length;
  const isStrikedThroughPricesEnabled = flight.priceBreakdown.showPriceStrikethrough && !isGeniusMerchBlockoutEnabled;

  const totalPrice = priceBreakdown;

  const formattedPricePerTravellerString = useMemo(
    () =>
      !!adultTravellerPrice
        ? formatPrice(
            roundPrice && adultTravellerPrice.travellerPriceBreakdown.totalRounded
              ? adultTravellerPrice.travellerPriceBreakdown.totalRounded
              : adultTravellerPrice.travellerPriceBreakdown.total,
            { decimalPlaces: roundPrice ? 0 : undefined }
          )
        : "",
    [adultTravellerPrice, formatPrice, roundPrice]
  );

  const strikedThroughPrice = useMemo(
    () =>
      isStrikedThroughPricesEnabled && roundPrice && priceBreakdown.totalWithoutDiscountRounded
        ? formatPrice(priceBreakdown.totalWithoutDiscountRounded, { decimalPlaces: 0 })
        : isStrikedThroughPricesEnabled && priceBreakdown.totalWithoutDiscount
        ? formatPrice(priceBreakdown.totalWithoutDiscount)
        : null,
    [
      formatPrice,
      isStrikedThroughPricesEnabled,
      roundPrice,
      priceBreakdown.totalWithoutDiscount,
      priceBreakdown.totalWithoutDiscountRounded
    ]
  );

  const priceAriaLabel = useMemo(() => {
    const priceLabel = formattedPricePerTravellerString;

    if (localizationUtils.isHungaryDepartingRyanAirFlight(flight.segments)) {
      return `${priceLabel} ${i18n.trans(t("flights_ryanair_hungary_sr_price_single"))}`;
    }

    if (isStrikedThroughPricesEnabled && strikedThroughPrice) {
      return i18n.trans(
        t("a11y_flights_deal_price_new", { variables: { new_price: formattedPricePerTravellerString } })
      );
    }

    return priceLabel;
  }, [flight.segments, formattedPricePerTravellerString, i18n, isStrikedThroughPricesEnabled, strikedThroughPrice]);

  const formattedTotalPrice = useMemo(
    () =>
      formatPrice(roundPrice && totalPrice.totalRounded ? totalPrice.totalRounded : totalPrice.total, {
        decimalPlaces: roundPrice ? 0 : undefined
      }),
    [formatPrice, roundPrice, totalPrice.totalRounded, totalPrice.total]
  );

  const priceExplanationAriaLabel = useMemo(() => {
    let priceExplanation = i18n.trans(t("flights_price_qualifier_total_for_all"));

    if (localizationUtils.isHungaryDepartingRyanAirFlight(flight.segments)) {
      priceExplanation = i18n.trans(
        t("flights_ryanair_hungary_bp_price_per", {
          variables: { total_price: formattedTotalPrice, num_travellers: travellersCount }
        })
      );
    } else if (isStrikedThroughPricesEnabled && priceBreakdown.totalWithoutDiscount) {
      return i18n.trans(
        t("a11y_flights_deal_price_total", {
          variables: {
            total_price: formattedTotalPrice,
            old_price: formatPrice(priceBreakdown.totalWithoutDiscount)
          }
        })
      );
    }

    return `${formattedTotalPrice} ${priceExplanation}`;
  }, [
    flight.segments,
    formatPrice,
    formattedTotalPrice,
    i18n,
    isStrikedThroughPricesEnabled,
    priceBreakdown.totalWithoutDiscount,
    travellersCount
  ]);

  const ariaExplanationAttributes = useMemo(() => {
    const travellersCount = adults + children.length;
    const attributes = {};

    if (travellersCount === 1) {
      // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
      attributes["aria-hidden"] = true;
    } else {
      // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
      attributes["aria-label"] = priceExplanationAriaLabel;
    }

    return attributes;
  }, [adults, children.length, priceExplanationAriaLabel]);

  const strikedThroughPriceContent = isStrikedThroughPricesEnabled && priceBreakdown.totalWithoutDiscount && (
    <>
      <Text variant={isMobile ? "body_2" : "small_1"} color="destructive" decoration="line-through">
        {strikedThroughPrice}
      </Text>
      {/**
       * To address the bug on RTL price display for multi-travellers
       * which the currency of the original price is shown within the
       * read-out of the discounted price
       *
       * More info: https://jira.booking.com/jira/browse/FPRICING-807
       */}
      {isRTL && <Text variant="body_2"> </Text>}
    </>
  );

  // flights_unified_price_tag -- start
  // ==========================================================================
  const shouldShowUnifiedPriceTag = useMemo(() => trackUnifiedPriceTag(), [trackUnifiedPriceTag]);

  const unifiedPriceTagProps = useMemo((): UnifiedPriceTagProps | undefined => {
    if (!shouldShowUnifiedPriceTag) return;

    const productId = flight.unifiedPriceBreakdown?.id;

    const strikethroughPrice =
      priceBreakdown.showPriceStrikethrough && !isGeniusMerchBlockoutEnabled
        ? roundPrice && priceBreakdown.totalWithoutDiscountRounded
          ? priceBreakdown.totalWithoutDiscountRounded
          : priceBreakdown.totalWithoutDiscount
        : undefined;

    const pricePerAdult = !!adultTravellerPrice
      ? roundPrice && adultTravellerPrice.travellerPriceBreakdown.totalWithoutDiscountRounded
        ? adultTravellerPrice.travellerPriceBreakdown.totalWithoutDiscountRounded
        : adultTravellerPrice.travellerPriceBreakdown.totalWithoutDiscount
      : undefined;

    const pricePerAdultExplanationProps = {
      attributes: {
        "data-testid": "flight_card_price_total_price",
        ...ariaExplanationAttributes
      }
    };

    const pricePerAdultExplanationTranslations = [
      (formattedPrice: string) =>
        i18n.trans(
          t("flights_sr_split_bounds_us_total", {
            variables: { price: formattedPrice }
          })
        )
    ];

    const attachedAncillaries = getAttachedAncillaries(flight.unifiedPriceBreakdown);

    return {
      productId,
      price: roundPrice && totalPrice.totalRounded ? totalPrice.totalRounded : totalPrice.total,
      priceProps: {
        attributes: { "aria-hidden": true, "data-testid": "flight_card_price_main_price" },
        variant: "headline_3"
      },
      priceAlignment: "end",
      strikethroughPrice,
      strikethroughPriceProps: { variant: isMobile ? "body_2" : "small_1" },
      strikethroughPricePosition: "top",
      pricePerAdult,
      pricePerAdultExplanationProps,
      pricePerAdultExplanationTranslations,
      shouldShowPricePerAdult: travellersCount > 1,
      shouldRoundPrice: roundPrice,
      attachedAncillaries
    };
  }, [
    adultTravellerPrice,
    ariaExplanationAttributes,
    i18n,
    isGeniusMerchBlockoutEnabled,
    isMobile,
    priceBreakdown,
    roundPrice,
    totalPrice,
    shouldShowUnifiedPriceTag,
    travellersCount,
    flight.unifiedPriceBreakdown
  ]);

  useEffect(() => {
    trackUnifiedPriceTagStages({
      withPriceBreakdownTooltip: false,
      withPriceStrikethrough:
        priceBreakdown.showPriceStrikethrough &&
        !isGeniusMerchBlockoutEnabled &&
        (roundPrice && priceBreakdown.totalWithoutDiscountRounded ? true : !!priceBreakdown.totalWithoutDiscount),
      withPricePerAdultExplanation:
        travellersCount > 1 &&
        !!adultTravellerPrice &&
        (roundPrice && adultTravellerPrice.travellerPriceBreakdown.totalWithoutDiscountRounded
          ? true
          : !!adultTravellerPrice.travellerPriceBreakdown.totalWithoutDiscount),
      withPriceExplanation: false,
      withPriceExplanationOnly: false,
      withUsOutboundInbound: true
    });
  }, [
    adultTravellerPrice,
    priceBreakdown.showPriceStrikethrough,
    priceBreakdown.totalWithoutDiscount,
    priceBreakdown.totalWithoutDiscountRounded,
    roundPrice,
    travellersCount,
    trackUnifiedPriceTagStages,
    isGeniusMerchBlockoutEnabled
  ]);
  // ==========================================================================
  // flights_unified_price_tag -- end

  return (
    <div>
      <Text variant="small_1" color="neutral_alt">
        {travellersCount > 1
          ? i18n.trans(t("flights_sr_split_bounds_us_round_trip_per"))
          : i18n.trans(t("flights_sr_split_bounds_us_round_trip"))}
      </Text>
      {shouldShowUnifiedPriceTag && unifiedPriceTagProps ? ( // flights_unified_price_tag
        <UnifiedPriceTag {...unifiedPriceTagProps} attributes={{ role: "region", "aria-label": priceAriaLabel }} />
      ) : (
        <>
          {travellersCount == 1 && strikedThroughPriceContent}
          <Frame
            direction="row"
            justifyContent="flex-end"
            attributes={{ role: "region", "aria-label": priceAriaLabel }}
          >
            <Title
              variant="headline_3"
              attributes={{ "aria-hidden": true, "data-testid": "flight_card_price_main_price" }}
              title={formattedPricePerTravellerString}
            />
          </Frame>
          <Text
            variant="small_1"
            color="neutral_alt"
            attributes={{
              "data-testid": "flight_card_price_total_price",
              ...ariaExplanationAttributes
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="end" gap={1}>
              {travellersCount > 1 && strikedThroughPriceContent}
              {travellersCount > 1 && (
                <Text variant="body_2">
                  {i18n.trans(
                    t("flights_sr_split_bounds_us_total", {
                      variables: { price: formattedTotalPrice }
                    })
                  )}
                </Text>
              )}
            </Stack>
          </Text>
        </>
      )}
    </div>
  );
};

export default FlightCardPriceOutbound;
